import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { AppDispatch, RootState } from '../store';
import { FaqItem } from '../../types/faq';

/**
 * Admin-managed, versioned FAQ content.
 *
 * The whole FAQ is stored as a single versioned document (docType `faq`) whose
 * `content` is a JSON-encoded array of {@link FaqItem}. This reuses the legal
 * documents' content-as-string + revision machinery verbatim. The slice owns
 * the (de)serialisation boundary: thunks parse the stored JSON into `items` and
 * serialise `items` back to JSON on save, so the rest of the app works with a
 * typed array rather than a string.
 *
 * The `/faq-content` route is authenticated-only (the FAQ screen lives behind
 * login), so this slice is never dispatched by guest identities.
 */

/** The single FAQ collection's docType, shared by the API catalogue and UI. */
export const FAQ_DOC_TYPE = 'faq';

interface FaqDocumentResponse {
	docType: string;
	content: string;
	version: number;
	updatedAt: string | null;
}

interface FaqContent {
	items: FaqItem[];
	version: number;
	updatedAt: string | null;
}

interface FaqDocumentsState extends FaqContent {
	loading: boolean;
	loaded: boolean;
	saving: boolean;
}

const initialState: FaqDocumentsState = {
	items: [],
	version: 0,
	updatedAt: null,
	loading: false,
	loaded: false,
	saving: false
};

/**
 * Parse a stored FAQ document into typed content. Malformed JSON (or a
 * non-array payload) degrades to an empty list rather than throwing, so a bad
 * record can never break the FAQ screen — the page falls back to its hardcoded
 * list when `items` is empty.
 */
function parseFaqDocument(doc: FaqDocumentResponse | undefined | null): FaqContent {
	const version = typeof doc?.version === 'number' ? doc.version : 0;
	const updatedAt = typeof doc?.updatedAt === 'string' ? doc.updatedAt : null;

	if (!doc || typeof doc.content !== 'string' || doc.content.trim().length === 0) {
		return { items: [], version, updatedAt };
	}

	try {
		const parsed = JSON.parse(doc.content);
		if (!Array.isArray(parsed)) {
			return { items: [], version, updatedAt };
		}
		const items = parsed
			.filter(
				(item): item is FaqItem =>
					Boolean(item) &&
					typeof item === 'object' &&
					typeof item.question === 'string' &&
					typeof item.answer === 'string'
			)
			.map((item, index) => ({
				id: typeof item.id === 'string' && item.id.length > 0 ? item.id : `faq-${index}`,
				question: item.question,
				answer: item.answer
			}));
		return { items, version, updatedAt };
	} catch {
		return { items: [], version, updatedAt };
	}
}

export const fetchFaqContent = createAsyncThunk<FaqContent, void, { dispatch: AppDispatch; state: RootState }>(
	'faqDocuments/fetch',
	async () => {
		const response = await API.get('inspirusapi', `/faq-content/${FAQ_DOC_TYPE}`, {});
		return parseFaqDocument(response as FaqDocumentResponse);
	},
	{
		// De-dupe concurrent/repeat dispatches: callers re-fetch on every mount so
		// freshly published FAQ content goes live without a deploy, but a request
		// already in flight is skipped to avoid redundant network calls.
		condition: (_arg, { getState }) => !getState().faqDocuments.loading
	}
);

export const saveFaqContent = createAsyncThunk<
	FaqContent,
	{ items: FaqItem[]; version: number },
	{ dispatch: AppDispatch; state: RootState }
>('faqDocuments/save', async ({ items, version }) => {
	const response = await API.put('inspirusapi', `/faq-content/${FAQ_DOC_TYPE}`, {
		headers: {},
		body: { content: JSON.stringify(items), version }
	});
	return parseFaqDocument(response as FaqDocumentResponse);
});

const faqDocumentsSlice = createSlice({
	name: 'faqDocuments',
	initialState,
	reducers: {
		resetFaqDocuments: () => initialState
	},
	extraReducers: (builder) => {
		builder.addCase(fetchFaqContent.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(fetchFaqContent.fulfilled, (state, action) => {
			state.items = action.payload.items;
			state.version = action.payload.version;
			state.updatedAt = action.payload.updatedAt;
			state.loading = false;
			state.loaded = true;
		});
		builder.addCase(fetchFaqContent.rejected, (state) => {
			state.loading = false;
			// Mark the slice as attempted so consumers that dispatch on
			// `!loaded && !loading` don't enter an infinite retry loop after a failed
			// fetch (e.g. when the backend route is not yet deployed → 404).
			state.loaded = true;
		});
		builder.addCase(saveFaqContent.pending, (state) => {
			state.saving = true;
		});
		builder.addCase(saveFaqContent.fulfilled, (state, action) => {
			state.items = action.payload.items;
			state.version = action.payload.version;
			state.updatedAt = action.payload.updatedAt;
			state.saving = false;
		});
		builder.addCase(saveFaqContent.rejected, (state) => {
			state.saving = false;
		});
	}
});

export const { resetFaqDocuments } = faqDocumentsSlice.actions;
export default faqDocumentsSlice.reducer;
